import * as React from 'react';
import { signalItemsViewRenderComplete, signalItemsViewNavigationStart } from '../../operations/pageLoad';
import { IFacet } from '../../models/Facet';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { errorFacet } from '../../facets/DemandFacets';
import { Fragment } from '../../utilities/components/Fragment';
import { IItemsScopeStateProps } from '../itemsScope/ItemsScope';
import { IOnCurrentItemKeyChangedProps } from '../itemsNavigator/ItemsNavigator';

/**
 * @internal
 */
export interface IPageLoadTrackerBaseProps {
    itemsScopeItemKey: string;
    itemKey: string | undefined;
    isReady: boolean;
    signalItemsViewRenderComplete: () => void;
    signalItemsViewNavigationStart: (payload: IOnCurrentItemKeyChangedProps) => void;
}

/**
 * @internal
 */
export interface IPageLoadTrackerBaseState {
    // Nothing.
}

/**
 * @internal
 */
export class PageLoadTrackerBase extends React.PureComponent<IPageLoadTrackerBaseProps, IPageLoadTrackerBaseState> {
    public render(): JSX.Element {
        return (
            <Fragment>{ this.props.children }</Fragment>
        );
    }

    public componentDidMount(): void {
        const {
            isReady,
            itemKey
        } = this.props;

        this.props.signalItemsViewNavigationStart({
            previousItemKey: undefined,
            currentItemKey: itemKey
        });

        if (isReady) {
            this.props.signalItemsViewRenderComplete();
        }
    }

    public componentDidUpdate(previousProps: IPageLoadTrackerBaseProps): void {
        const {
            isReady,
            itemKey
        } = this.props;

        if (itemKey !== previousProps.itemKey) {
            this.props.signalItemsViewNavigationStart({
                previousItemKey: previousProps.itemKey,
                currentItemKey: itemKey
            });
        }

        if ((!previousProps.isReady || itemKey !== previousProps.itemKey) && isReady) {
            this.props.signalItemsViewRenderComplete();
        }
    }
}

/**
 * @internal
 */
export interface IPageLoadTrackerProps {
    itemsScopeItemKey: string;
    itemKey?: string;
    facets: IFacet<{}>[];
    isReady?: boolean;
}

/**
 * @internal
 */
export const PageLoadTracker = connectToItemsScope()(
    connectToItemCache((itemCache: IItemCache, props: IPageLoadTrackerProps & IItemsScopeStateProps):
        Pick<IPageLoadTrackerBaseProps, 'isReady' | 'itemsScopeItemKey'> => {
        const {
            itemKey,
            itemsScopeItemKey,
            isReady: parentIsReady = true
        } = props;

        const isReady = props.facets.every((facet: IFacet<{}>) => itemCache.isItemFacetResolved(facet, itemKey));
        const isError = itemCache.isItemFacetResolved(errorFacet, itemKey);

        return {
            itemsScopeItemKey,
            isReady: parentIsReady && (isReady || isError)
        };
    }, (dispatch: IItemCacheDispatch, props: IPageLoadTrackerProps & IItemsScopeStateProps) => {
        const {
            itemsScopeItemKey
        } = props;

        return {
            signalItemsViewRenderComplete: () => {
                dispatch(signalItemsViewRenderComplete(itemsScopeItemKey));
            },
            signalItemsViewNavigationStart: (payload: IOnCurrentItemKeyChangedProps) => {
                dispatch(signalItemsViewNavigationStart({
                    ...payload,
                    itemsScopeItemKey
                }));
            }
        };
    })(PageLoadTrackerBase)
);
